/**
 * 스캔 세션 (SC-010 인식 결과 · SC-011 요약) — design 브랜치 ScanSessionContext 의
 * 실 데이터 이식 (2026-08-24). 카드 스캔 모드에서 확정된 카드가 쌓이고,
 * 요약 화면(/scan/summary)에서 편집·일괄 담기한다.
 *
 * ⚠ **일부러 저장하지 않는다.** 스캔 결과는 저장 전까지 임시물이고, FN-031 이
 *   "저장 전 이탈하면 분실된다"고 경고하기로 한 것이기 때문이다(폐기 확인이 그 자리).
 *
 * FN-026 연속 인식 — 같은 카드를 또 인식해도 별개 항목으로 쌓는다(실제로 2장일 수
 * 있다). 요약에서 수량 스테퍼로 합치는 것은 사용자 몫.
 */
import { create } from "zustand";
import type { CardItem } from "../api/card";

export interface ScanSessionItem {
  id: string;
  /** 매칭 확정 시점의 카탈로그 카드 — 세션 수명 동안 스냅샷으로 충분하다 */
  card: CardItem;
  /** SC-011 수량 스테퍼 */
  qty: number;
  /** SC-011 유형 드롭다운 — null = 기본판(대표 시세). 값은 VariantPrice.finish */
  finish: string | null;
  /**
   * 유형 선택 시점에 카드 상세(variantPrices)에서 굳힌 1장 값 — finish=null 이면 null.
   * 합계 계산이 카드마다 상세를 다시 부르지 않게 선택 시점 값을 세션에 박아 둔다.
   */
  unitOverride: number | null;
}

interface ScanSessionState {
  items: ScanSessionItem[];
  /** 반환값 = 항목 id — 지연 하이드레이션(hydrateCard)이 이 id 로 채운다 */
  addCard: (card: CardItem) => string;
  removeItem: (id: string) => void;
  updateItem: (id: string, patch: Partial<Pick<ScanSessionItem, "qty" | "finish" | "unitOverride">>) => void;
  /**
   * 지연 하이드레이션 (2026-08-27) — 확정 시 사이드카(이름·번호)로 즉시 담고,
   * 카탈로그 API(이미지·시세·언어)는 뒤에서 채운다. 이미 지워진 항목이면 무시.
   */
  hydrateCard: (id: string, card: CardItem) => void;
  clear: () => void;
}

let seq = 0;

export const useScanSessionStore = create<ScanSessionState>((set) => ({
  items: [],
  addCard: (card) => {
    const id = `scan-${++seq}`;
    set((s) => ({
      items: [
        { id, card, qty: 1, finish: null, unitOverride: null },
        ...s.items, // 최신이 앞 — 인식 레일이 방금 잡은 카드를 먼저 보여준다
      ],
    }));
    return id;
  },
  hydrateCard: (id, card) =>
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? { ...it, card } : it)),
    })),
  removeItem: (id) => set((s) => ({ items: s.items.filter((it) => it.id !== id) })),
  updateItem: (id, patch) =>
    set((s) => ({
      items: s.items.map((it) => (it.id === id ? { ...it, ...patch } : it)),
    })),
  clear: () => set({ items: [] }),
}));

/** SC-010 누적 합계 · SC-011 평가액 합계. 시세 모르는 카드는 합계에서 빠진다. */
export interface ScanTotals {
  cardCount: number;
  totalAmt: number;
  /** 시세를 모르는 장수 — 합계가 '전부'가 아님을 화면이 밝혀야 한다 */
  unknownCount: number;
}

/** 1장 값 — 유형을 골랐으면 그 시점에 굳힌 값, 아니면 대표 시세(listPrice). */
export function scanUnitAmount(it: ScanSessionItem): number | null {
  return it.finish !== null ? it.unitOverride : it.card.listPrice.amount;
}

export function scanTotals(items: readonly ScanSessionItem[]): ScanTotals {
  let totalAmt = 0;
  let cardCount = 0;
  let unknownCount = 0;
  for (const it of items) {
    cardCount += it.qty;
    const unit = scanUnitAmount(it);
    if (unit === null) unknownCount += it.qty;
    else totalAmt += unit * it.qty;
  }
  return { cardCount, totalAmt, unknownCount };
}
